"use client";

import type { MediaImageCandidate } from "@/lib/media-image";
import { apiUrl } from "@/lib/api";
import { withAdminSessionHeaders } from "@/lib/adminSession";
import { asRecord } from "@/hooks/webPlayerUtils";

export type MediaProviderRequestError = Error & {
  status: number;
  responseText: string;
};

export type MediaProviderSearchTrack = {
  id: string;
  name: string;
  uri: string;
  artists: { name: string }[];
  album: { name: string; images?: { url: string }[] } | null;
  duration_ms: number;
  is_restricted: boolean;
  restriction_reason: string | null;
  restriction_reasons: string[];
};

export type MediaProviderPlaylist = {
  id: string;
  name: string;
  description: string | null;
  imageUrl: string | null;
  images: MediaImageCandidate[];
  trackCount: number;
  ownerId: string | null;
  ownerName: string | null;
  collaborative: boolean;
  isPublic: boolean | null;
};

export function mediaProviderErrorMessage(error: unknown, fallback: string) {
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

export function isMediaProviderRequestError(
  error: unknown,
): error is MediaProviderRequestError {
  return (
    error instanceof Error &&
    typeof (error as { status?: unknown }).status === "number" &&
    typeof (error as { responseText?: unknown }).responseText === "string"
  );
}

function createMediaProviderRequestError(
  status: number,
  responseText: string,
  fallbackMessage: string,
) {
  const message =
    responseText.trim() || fallbackMessage || `Request failed (HTTP ${status})`;
  const error = new Error(message) as MediaProviderRequestError;
  error.name = "MediaProviderRequestError";
  error.status = status;
  error.responseText = responseText;
  return error;
}

export async function fetchMediaProviderJson<T>(
  endpoint: string,
  init: RequestInit = {},
  fallbackMessage = "Request failed",
): Promise<T> {
  const response = await fetch(
    apiUrl(endpoint),
    withAdminSessionHeaders({
      credentials: "include",
      ...init,
    }),
  );

  if (!response.ok) {
    const responseText = await response.text().catch(() => "");
    throw createMediaProviderRequestError(
      response.status,
      responseText,
      `${fallbackMessage} (HTTP ${response.status})`,
    );
  }

  return response.json() as Promise<T>;
}

export function extractMediaResponseItems<T>(payload: unknown): T[] {
  const record = asRecord(payload);
  return Array.isArray(record.items) ? (record.items as T[]) : [];
}

export function normalizeMediaPlaylists(
  payload: unknown,
  collaborativePlaylistsByDefault: boolean,
): MediaProviderPlaylist[] {
  return extractMediaResponseItems<unknown>(payload).map((rawPlaylist) => {
    const playlist = asRecord(rawPlaylist);
    const imageCandidates = Array.isArray(playlist.images)
      ? (playlist.images as unknown[])
      : [];
    const images: MediaImageCandidate[] = imageCandidates
      .map((rawImage) => {
        const image = asRecord(rawImage);
        return {
          url: typeof image.url === "string" ? image.url : "",
          width:
            typeof image.width === "number" && Number.isFinite(image.width)
              ? image.width
              : null,
          height:
            typeof image.height === "number" && Number.isFinite(image.height)
              ? image.height
              : null,
        };
      })
      .filter((image) => Boolean(image.url));

    const tracksRecord = asRecord(playlist.tracks);
    const ownerRecord = asRecord(playlist.owner);

    return {
      id: typeof playlist.id === "string" ? playlist.id : "",
      name: typeof playlist.name === "string" ? playlist.name : "Untitled",
      description:
        typeof playlist.description === "string" ? playlist.description : null,
      imageUrl: images[0]?.url ?? null,
      images,
      trackCount:
        typeof tracksRecord.total === "number" &&
        Number.isFinite(tracksRecord.total)
          ? tracksRecord.total
          : 0,
      ownerId: typeof ownerRecord.id === "string" ? ownerRecord.id : null,
      ownerName:
        typeof ownerRecord.display_name === "string"
          ? ownerRecord.display_name
          : typeof ownerRecord.id === "string"
            ? ownerRecord.id
            : null,
      collaborative: collaborativePlaylistsByDefault
        ? true
        : Boolean(playlist.collaborative),
      isPublic: typeof playlist.public === "boolean" ? playlist.public : null,
    };
  });
}

export function normalizeMediaSearchTracks(
  payload: unknown,
): MediaProviderSearchTrack[] {
  const tracksRecord = asRecord(asRecord(payload).tracks);
  const rawItems = Array.isArray(tracksRecord.items)
    ? (tracksRecord.items as unknown[])
    : [];

  return rawItems.map((rawTrack) => {
    const track = asRecord(rawTrack);
    const albumRecord =
      track.album && typeof track.album === "object" ? asRecord(track.album) : null;
    const rawArtists = Array.isArray(track.artists)
      ? (track.artists as unknown[])
      : [];
    const rawImages = Array.isArray(albumRecord?.images)
      ? (albumRecord.images as unknown[])
      : [];
    const rawRestrictionReasons = Array.isArray(track.restriction_reasons)
      ? (track.restriction_reasons as unknown[])
      : [];

    return {
      id: typeof track.id === "string" ? track.id : "",
      name: typeof track.name === "string" ? track.name : "Untitled",
      uri: typeof track.uri === "string" ? track.uri : "",
      artists: rawArtists
        .map((rawArtist) => {
          const artist = asRecord(rawArtist);
          return {
            name: typeof artist.name === "string" ? artist.name : "",
          };
        })
        .filter((artist) => Boolean(artist.name)),
      album: albumRecord
        ? {
            name:
              typeof albumRecord.name === "string" ? albumRecord.name : "Album",
            images: rawImages
              .map((rawImage) => {
                const image = asRecord(rawImage);
                return {
                  url: typeof image.url === "string" ? image.url : "",
                };
              })
              .filter((image) => Boolean(image.url)),
          }
        : null,
      duration_ms:
        typeof track.duration_ms === "number" && Number.isFinite(track.duration_ms)
          ? track.duration_ms
          : 0,
      is_restricted: track.is_restricted === true,
      restriction_reason:
        typeof track.restriction_reason === "string"
          ? track.restriction_reason
          : null,
      restriction_reasons: rawRestrictionReasons.filter(
        (value): value is string =>
          typeof value === "string" && value.trim().length > 0,
      ),
    };
  });
}
